import { mapApiToAppointment, type RawAppointment } from 'core/appointments/infra/appointmentMapper'
import { type CommodityItem } from 'core/domain/CommodityItem'
import { type EquipmentSubtypes } from 'core/domain/Load'
import { type Mode } from 'core/domain/Mode'
import { type Shipper } from 'core/domain/Shipper'
import { type StopType, type Stop } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { isNotEmpty } from 'core/utils/string'

import { type AgnosticShipment, type AgnosticShipmentStatus } from '../domain/AgnosticShipment'
import { type Driver, type LastLocation, type ShipmentIdentifier } from '../domain/BaseShipment'

export type RawDriver = {
  carrier: string
  email: string | null
  name: string | null
  phone_number: string | null
  roles: Array<string> | null
  uuid: string
}

type RawItem = {
  po_number?: string | null
  so_number?: string | null
  commodity: string | null
  height?: string | null
  length?: string | null
  weight?: string | null
  width?: string | null
}

type RawLastLocation = {
  city: string
  entered_by: string | null
  latitude: string
  longitude: string
  source: string
  state: string
  timestamp: string
  uuid: string
}

type RawShipper = {
  company_name: string
  email: string
  logo_email_url: string | null
  phone_number: string | null
  business: string | null
}

type RawAgnosticStopCheck = {
  check_in: {
    done_at: string
  } | null
  check_out: {
    done_at: string
  } | null
}

type RawStop = {
  id: number
  city: string
  country: string
  date: string | null
  date_source?: string | null
  state: string
  zipcode: string
  stop_type?: StopType
  stop_index?: number
  appointment: RawAppointment | null
  facility?: {
    company_name: string
  }
} & RawAgnosticStopCheck

export type RawAgnosticShipment = {
  bol_number: string | null
  cancel_reason: string | null
  canceled_at: string | null
  canceled_by: string | null
  carrier_ref_numbers: string[] | null
  carrier_update_request_sent_at: string | null
  dispatcher: string | null
  carrier_1xp_link: string
  driver: RawDriver | null
  equipment_length: string | null
  equipment_type: string
  equipment_subtypes: Array<EquipmentSubtypes> | null
  hazmat: boolean
  is_loadsmart_carrier: boolean
  items: Array<RawItem>
  last_location?: RawLastLocation | null
  mileage: number
  mode: string
  po_numbers: string[] | null
  po_number: string
  shipper: RawShipper
  shipper_guide_id: string
  shipper_ref_number: string | null
  so_number: string
  status: string
  stops: Array<RawStop>
  tracking_link: string
  tractor_number: string | null
  uuid: string
}

export function mapApiToItems(items: Array<RawItem>): Array<CommodityItem> {
  return items.map(item => ({
    commodity: item.commodity ?? undefined,
    height: item.height ? Number(item.height) : undefined,
    length: item.length ? Number(item.length) : undefined,
    weight: item.weight ? Number(item.weight) : undefined,
    width: item.width ? Number(item.width) : undefined,
  }))
}

export function mapApiToDriver(driver: RawDriver | null): Driver | undefined {
  if (!driver) return undefined

  const { carrier, email, name, phone_number, roles, uuid } = driver
  return {
    carrier,
    email: email ?? undefined,
    name: name ?? undefined,
    phoneNumber: phone_number ?? undefined,
    roles: roles ?? undefined,
    uuid: uuid,
  }
}

export function mapApiToIdentifiers(shipment: RawAgnosticShipment): Array<ShipmentIdentifier> {
  const { shipper_ref_number, shipper_guide_id } = shipment

  let identifiers: Array<ShipmentIdentifier> = []

  if (shipper_ref_number) identifiers.push({ label: 'Ref#', values: [String(shipper_ref_number)] })
  if (shipper_guide_id) identifiers.push({ label: '', values: [`#${String(shipper_guide_id)}`] })

  return identifiers
}

export function mapApiToLastLocation(
  lastLocation?: RawLastLocation | null
): LastLocation | undefined {
  if (!lastLocation) return undefined

  return {
    city: lastLocation.city,
    enteredBy: lastLocation.entered_by ?? undefined,
    latitude: lastLocation.latitude,
    longitude: lastLocation.longitude,
    source: lastLocation.source,
    state: lastLocation.state,
    timestamp: new Date(lastLocation.timestamp),
    uuid: lastLocation.uuid,
  }
}

export function mapApiToShipper(shipper: RawShipper): Shipper {
  const { company_name, email, logo_email_url, phone_number } = shipper
  return {
    companyName: company_name,
    email: email,
    logoUrl: logo_email_url && isNotEmpty(logo_email_url) ? logo_email_url : undefined,
    phoneNumber: phone_number && isNotEmpty(phone_number) ? phone_number : undefined,
  }
}

export function mapApiToStopChecks(stop: RawStop) {
  let checkedInAt
  let checkedOutAt
  const { check_in, check_out } = stop

  if (check_in) checkedInAt = new Date(check_in.done_at)
  if (check_out) checkedOutAt = new Date(check_out.done_at)

  return {
    checkedInAt,
    checkedOutAt,
  }
}

export function mapApiToStops(stops: Array<RawStop>): Array<Stop> {
  return stops.map(stop => ({
    city: stop.city,
    country: stop.country,
    date: stop.date ?? undefined,
    state: stop.state,
    zipCode: stop.zipcode,
    appointment: mapApiToAppointment(stop.appointment),
    facilityName: stop.facility?.company_name,
    ...mapApiToStopChecks(stop),
  }))
}

function mapApiToEquipmentSubType(subEquipments: Array<EquipmentSubtypes> | null) {
  if (!subEquipments) {
    return undefined
  }

  const [subEquipment] = subEquipments

  const equipmentSubtypes = {
    regular_flatbed: 'Regular Flatbed',
    conestoga: 'Conestoga',
    step_deck: 'Step Deck',
    hotshot: 'Hotshot',
    double_drop: 'Double Drop',
    gooseneck: 'Gooseneck',
    low_boy: 'Low Boy',
    maxi: 'Maxi',
    power_only: 'Power Only',
    removable_gooseneck: 'Removable Gooseneck',
    conestoga_step_deck: 'Conestoga Step Deck',
  }

  return equipmentSubtypes[subEquipment]
}

export function mapApiToAgnosticShipment(shipment: RawAgnosticShipment): AgnosticShipment {
  const {
    carrier_1xp_link,
    driver,
    items,
    last_location,
    mileage,
    mode,
    shipper,
    stops,
    status,
    tractor_number,
    uuid,
    equipment_length,
    equipment_subtypes,
    so_number,
    po_number,
    bol_number,
  } = shipment

  const parsedDriver = mapApiToDriver(driver)
  const identifiers = mapApiToIdentifiers(shipment)
  const lastLocation = mapApiToLastLocation(last_location)
  const parsedItems = mapApiToItems(items)
  const parsedShipper = mapApiToShipper(shipper)
  const parsedStops = mapApiToStops(stops)

  return {
    equipmentType: shipment.equipment_type as TruckType,
    shipmentURL: carrier_1xp_link,
    driver: parsedDriver,
    identifiers,
    items: parsedItems,
    mileage,
    lastLocation,
    mode: mode as Mode,
    status: status as AgnosticShipmentStatus,
    shipper: parsedShipper,
    stops: parsedStops,
    tractorNumber: tractor_number || undefined,
    uuid,
    equipmentLength: equipment_length || undefined,
    equipmentSubType: mapApiToEquipmentSubType(equipment_subtypes),
    poNumbers: po_number.length > 0 ? po_number?.split(',') : undefined,
    soNumbers: so_number.length > 0 ? so_number?.split(',') : undefined,
    bolNumber: bol_number,
  }
}
